import { Component, computed, inject, input, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UiModule } from '@components/ui/ui-module';
import { CardBinder, CardCondition } from '@models/binder.model';
import { BinderService } from '@services/binder.service';
import { Router } from '@services/route.service';
import { tap, switchMap, forkJoin, of } from 'rxjs';
import { Card as CardService } from '@services/card.service';
import { CardSet, Card as CardM, Variante, Rarity, MarketPlace, CardPrice } from '@models/card.model';
import { CommonModule, DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { Global } from '@services/global.service';
import { NotificationService } from '@services/notification.service';
import { Menu } from '@services/menu.service';
import { ScannerService } from '@services/scanner.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CtaShare } from '@components/partials/cta-share/cta-share';
import { PricesGrid } from '@components/partials/card/prices-grid/prices-grid';


@Component({
  selector: 'lgb-card',
  imports: [
    CommonModule,
    UiModule,
    RouterModule,
    DatePipe,
    UpperCasePipe,
    DecimalPipe,
    CtaShare,
    PricesGrid
  ],
  templateUrl: './card.html',
  styleUrl: './card.css',
})
export class Card implements OnDestroy{
  readonly router=inject(Router);
  readonly menu=inject(Menu);
  readonly scanS=inject(ScannerService);
  readonly route=inject(ActivatedRoute);
  readonly binderS=inject(BinderService);
  readonly cardS=inject(CardService);
  readonly globalS=inject(Global);
  readonly notify=inject(NotificationService);

  //data
  readonly cardBinder=signal<CardBinder>(null);
  readonly card=signal<CardM>(null);
  readonly cardSet=signal<CardSet>(null);
  readonly inventory=signal<CardBinder[]>([]);
  readonly prices=signal<CardPrice[]>(null);
  readonly conditions=signal<CardCondition[]>([]);
  readonly markets=signal<MarketPlace[]>([]);
  readonly variantes=signal<Variante[]>([]);
  readonly rarities=signal<Rarity[]>([]);
  readonly backHisto=signal(false);                 //indique si on doit utiliser l'historique pour retourner en arrière
  readonly backTo=signal<string>(null);             //fournis une uri interne pour le retour
  readonly share=signal<string>(null);
  readonly admin=computed(() => this.share()==null);

  readonly variante=computed(() => ((this.cardBinder().variante)? this.variantes().find(v => v.ref==this.cardBinder().variante) : null));
  readonly cond=computed(() => ((this.cardBinder())? this.getCondition(this.cardBinder().condition) : null));

  //ui
  readonly loading=signal(false);
  readonly loadingPrice=signal(false);
  readonly name=computed(() => this.cardS.getNameFeat(this.card(), this.cardBinder().lang));
  readonly inventoryPage=signal<CardBinder[]>([]);
  readonly length: number=5;

  constructor(){
    this.loading.set(true);

    //subscribe à la modification de carte
    this.scanS.resultRefreshCard
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(() => this.router.navigate('collect-binder', [this.cardBinder()?.binder], {active: 'cards'}));

    //subscribe queryparam
    this.route.queryParamMap.subscribe(params => {
      this.backHisto.set(params.get('back') && params.get('back')=='histo');
      if(params.get('backTo'))
        this.backTo.set(params.get('backTo'));
      this.share.set(params.get("share"));
    });
    
    this.route.paramMap
      .pipe(
        switchMap(params => forkJoin({
          init: this.getCard$(params.get('ident')),
          globals: this.globalS.init()
        }))
      )
      .subscribe(response => {
        const markets: MarketPlace[]=response.globals.marketplaces
          .filter((m: MarketPlace) => m.type=='market')
          .sort((a: MarketPlace, b: MarketPlace) => a.index-b.index);
        this.markets.set(markets);
        this.conditions.set(response.globals.conditions);
        this.variantes.set(response.globals.variantes);
        this.cardBinder.set(response.init.cardBinder);
        this.rarities.set(response.globals.rarities);
        this.card.set(response.init.cardIndex);
        this.cardSet.set(response.init.cardSet);
        this.inventory.set(response.init.inventory);
        this.menu.setAction('card', this.cardBinder().ident);
        this.loading.set(false);
      });
  }

  getCard$(ident: string){
    if(this.share())
      return this.binderS.getSharedCard(ident, this.share());
    else
      return this.binderS.getCard(ident);
  }

  ngOnDestroy(){
    this.menu.setAction('binder');
  }

  getCondition(condition: string){
    return this.conditions().find(c => c.ref==condition);
  }

  priceMarket(market: MarketPlace){
    const price: CardPrice=this.cardBinder().prices?.find(p => p.marketplace==market.ref);
    return ((price)? price.amount : null); 
  }

  search(){
    return (q: string, start: number, length: number) => of(this.inventory().slice(start, start+length));
  }

  rmCard(){
    if(confirm('Voulez-vous supprimer cette carte ?')){
      this.loading.set(true);
      this.binderS.rmCard(this.cardBinder().ident)
        .subscribe(() => {
          this.notify.notifySuccess('Carte supprimée.');
          this.loading.set(false);
          this.router.navigate('collect-binder', [this.cardBinder().binder], {active: 'cards'});
        });
    }
  }

  getRarity(){
    return this.rarities().find(r => r.ref==this.card().rarity);
  }

  toggleFavoriteCard(){
    this.binderS.toggleFavoriteCard(this.cardBinder().ident)
      .subscribe(card => this.cardBinder().isFavorite=card.isFavorite);
  }

  getUrlBack(){
    if(!this.backTo())
      return this.router.parse('collect-binder', [this.cardBinder().binder]);
    else
      return this.backTo();
  }

  getQueryParamsBack(){
    return ((this.backTo())? {} : {active: 'cards'});
  }

  getFragmentBack(){
    return ((this.backTo())? null : this.cardBinder().ident);
  }

  loadPrice(){
    this.loadingPrice.set(true);
    this.cardS.getPricesCardBinder(this.cardBinder().ident)
      .subscribe(prices => {
        this.prices.set(prices);
        this.loadingPrice.set(false);
      });
  }

}
